import { InjectionToken } from '@angular/core';

const ITEM_SELECTOR =
  ':is([role="menuitem"], [role="menuitemcheckbox"]):not([aria-disabled="true"]):not(.disabled):not(.locked)';

/** The menu that owns a submenu: picking an item hands focus back to its trigger first. */
export interface MenuHost {
  restoreTriggerFocus(): void;
}

export const MENU_HOST = new InjectionToken<MenuHost>('MENU_HOST');

/**
 * Whether a click inside a menu panel lands on an item that runs an action
 * (not a submenu trigger, not a disabled or locked item).
 */
export function isActionClick(event: Event): boolean {
  const target = event.target as Element | null;
  const item = target?.closest?.('[role="menuitem"], [role="menuitemcheckbox"]');
  if (!item || item.getAttribute('aria-haspopup')) return false;
  return item.getAttribute('aria-disabled') !== 'true';
}

/** The enabled items of one menu panel, in order. */
export function menuItems(panel: HTMLElement | null | undefined): HTMLElement[] {
  return panel ? Array.from(panel.querySelectorAll<HTMLElement>(ITEM_SELECTOR)) : [];
}

/**
 * Moves focus among a menu's items for ArrowDown/ArrowUp/Home/End, wrapping at
 * the ends. Returns true when the key was handled.
 */
export function moveMenuFocus(panel: HTMLElement | null | undefined, key: string): boolean {
  const items = menuItems(panel);
  if (!items.length) return false;
  const current = items.indexOf(document.activeElement as HTMLElement);
  let next: number;
  switch (key) {
    case 'ArrowDown':
      next = current < 0 ? 0 : (current + 1) % items.length;
      break;
    case 'ArrowUp':
      next = current < 0 ? items.length - 1 : (current - 1 + items.length) % items.length;
      break;
    case 'Home':
      next = 0;
      break;
    case 'End':
      next = items.length - 1;
      break;
    default:
      return false;
  }
  items[next].focus();
  return true;
}
